import type { DeliverableFormat, MaterialCategory, SubtaskKind } from './types.js'

export const materialCategories: MaterialCategory[] = [
  'Mídia OFF',
  'Mídia ON',
  'DOOH',
  'Textos',
  'Tabloides',
  'Mídias Com Ofertas',
  'Criações Institucionais',
]

export const materialOperations = ['Assaí', 'Gitanes', 'Menil'] as const
export const enabledMaterialOperations = ['Assaí'] as const
export const materialTimeUnits = ['minutos', 'horas', 'dias úteis'] as const
export const materialDimensionUnits = ['cm', 'px', 'm'] as const
export const materialFileExtensions = ['PNG', 'JPEG', 'GIF', 'PDF', 'MP4', 'MOV', 'TIFF'] as const
export const materialDurationOptions = [5, 7, 8, 10, 15, 20, 30, 40, 45, 60] as const
export const materialDieCutOptions = ['COM', 'SEM'] as const
export const materialMockupOptions = ['SIM', 'NÃO'] as const
export const materialTextCharacterOptions = [20, 30, 35, 90] as const
export const materialHubs = ['Matriz', 'Hub Aricanduva', 'Hub Penha', 'Hub RJ', 'Hub CoNo', 'Hub Salvador', 'Hub Florida'] as const
export const materialRegionalHubs = materialHubs.filter(hub => hub !== 'Matriz')

export type MaterialOperation = typeof materialOperations[number]
export type MaterialTimeUnit = typeof materialTimeUnits[number]
export type MaterialDimensionUnit = typeof materialDimensionUnits[number]
export type MaterialFileExtension = typeof materialFileExtensions[number]
export type MaterialDieCut = typeof materialDieCutOptions[number]
export type MaterialMockup = typeof materialMockupOptions[number]
export type MaterialHub = typeof materialHubs[number]

export interface MaterialDto {
  id: string
  operation: string
  materialCategory: MaterialCategory
  materialName: string
  hubs: string[]
  textCharacterCount: number | null
  estimatedTimeValue: number | null
  estimatedTimeUnit: string | null
  dimensionUnit: string | null
  width: number | null
  height: number | null
  durationSeconds: number | null
  fileExtension: string | null
  fileSize: string | null
  dieCut: string | null
  hasMockup: boolean | null
}

export interface MaterialSubmission {
  operation: MaterialOperation
  materialCategory: MaterialCategory
  textCharacterCount: number | null
  materialName: string
  estimatedTimeValue: number | null
  estimatedTimeUnit: MaterialTimeUnit | null
  dimensionUnit: MaterialDimensionUnit | null
  width: number | null
  height: number | null
  durationSeconds: number | null
  fileExtension: MaterialFileExtension | null
  fileSize: string | null
  dieCut: MaterialDieCut | null
  mockup: MaterialMockup | null
  hubs: MaterialHub[] | null
}

export interface MaterialApiErrorBody {
  ok?: false
  code?: string
  message?: string
  retryAfter?: number | null
}

const categoryFromDatabase: Record<string, MaterialCategory> = {
  'Mídia On': 'Mídia ON',
  'Mídia ON': 'Mídia ON',
  'Mídia Off': 'Mídia OFF',
  'Mídia OFF': 'Mídia OFF',
  DOOH: 'DOOH',
  Textos: 'Textos',
  Tabloides: 'Tabloides',
  'Mídias Com Ofertas': 'Mídias Com Ofertas',
  'Criações Institucionais': 'Criações Institucionais',
}

const categoryToDatabase: Record<MaterialCategory, string> = {
  'Mídia ON': 'Mídia On',
  'Mídia OFF': 'Mídia Off',
  DOOH: 'DOOH',
  Textos: 'Textos',
  Tabloides: 'Tabloides',
  'Mídias Com Ofertas': 'Mídias Com Ofertas',
  'Criações Institucionais': 'Criações Institucionais',
}

export function normalizeMaterialCategory(value: unknown): MaterialCategory | null {
  return typeof value === 'string' ? categoryFromDatabase[value.trim()] ?? null : null
}

export function databaseMaterialCategory(category: MaterialCategory) {
  return categoryToDatabase[category]
}

function materialKey(value: string) {
  return value.trim().toLocaleLowerCase('pt-BR')
}

export function materialCatalogFromDatabase(materials: MaterialDto[]): Record<MaterialCategory, string[]> {
  const catalog = {} as Record<MaterialCategory, string[]>
  materialCategories.forEach(category => { catalog[category] = [] })
  const seen = new Set<string>()
  materials.forEach(material => {
    const name = material.materialName.trim()
    const key = materialKey(name)
    if (!name || seen.has(key)) return
    catalog[material.materialCategory].push(name)
    seen.add(key)
  })
  return catalog
}

export function materialCategoryLabel(category: MaterialCategory) {
  if (category === 'Mídia ON') return 'Mídia On'
  if (category === 'Mídia OFF') return 'Mídia Off'
  return category
}

export function defaultSubtaskPresentation(category: MaterialCategory): { format: DeliverableFormat; kind: SubtaskKind } {
  switch (category) {
    case 'Mídia ON': return { format: 'Anúncio', kind: 'Criação' }
    case 'Mídia OFF': return { format: 'Jornal', kind: 'Criação' }
    case 'DOOH': return { format: 'LED', kind: 'Criação' }
    case 'Textos': return { format: 'Outro', kind: 'Redação' }
    case 'Tabloides': return { format: 'Jornal', kind: 'Criação' }
    case 'Mídias Com Ofertas':
    case 'Criações Institucionais': return { format: 'Outro', kind: 'Criação' }
  }
}

function normalizedComparable(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('pt-BR')
}

export function preferredMaterialOperation(clientName?: string): MaterialOperation | '' {
  return clientName && normalizedComparable(clientName) === 'assai' ? 'Assaí' : ''
}

export function preferredMaterialHub(campaignOperation?: string): MaterialHub | '' {
  if (!campaignOperation) return ''
  const normalized = normalizedComparable(campaignOperation)
  if (normalized === 'hub cono') return 'Hub CoNo'
  if (normalized === 'hub ne' || normalized === 'hub leste') return ''
  return materialHubs.find(hub => normalizedComparable(hub) === normalized) ?? ''
}

export function materialSubmissionPayload(submission: MaterialSubmission) {
  return { ...submission, materialCategory: databaseMaterialCategory(submission.materialCategory) }
}
